import { useState, useEffect } from "react";
import { ArrowUpRight, Radio, ScanLine, CircleAlert, SquareParking } from "lucide-react";
import { foApi } from "@/webapp/fleet-owners/api";
import { PageHead, Panel, Kpi, Tag, EmptyState, Button, SEVERITY, resTone, money, when } from "@/webapp/fleet-owners/ui";

const OUT_TONE = { entry: "ok", exit: "info", not_onsite: "warn", already_onsite: "warn", duplicate: "mute" };

function Attention({ alerts, go }) {
  if (!alerts.length) return <p className="px-5 py-8 text-sm text-slate-400" data-testid="fo-cc-attention-empty">Nothing needs attention. Expiries, overstays and gate flags will show up here.</p>;
  return (
    <ul className="divide-y divide-slate-100" data-testid="fo-cc-attention">
      {alerts.slice(0, 6).map((a) => (
        <li key={a.key}>
          <button data-testid={`fo-cc-alert-${a.key}`} onClick={() => go(a.tab)} className="flex w-full items-start gap-3 px-5 py-3 text-left transition-colors hover:bg-slate-50">
            <Tag tone={SEVERITY[a.severity]}>{a.severity}</Tag>
            <span className="min-w-0 flex-1"><span className="block truncate text-sm font-semibold text-[#0A234A]">{a.title}</span><span className="block truncate text-xs text-slate-500">{a.detail}</span></span>
            <ArrowUpRight className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-300" />
          </button>
        </li>
      ))}
    </ul>
  );
}

function ResList({ rows, empty, testid }) {
  if (!rows.length) return <p className="px-5 py-8 text-sm text-slate-400" data-testid={`${testid}-empty`}>{empty}</p>;
  return (
    <ul className="divide-y divide-slate-100" data-testid={testid}>
      {rows.map((r) => (
        <li key={r.id} className="flex items-center gap-3 px-5 py-3 text-sm">
          <span className="font-mono text-xs font-semibold text-[#0A234A]">{r.ref}</span>
          <span className="min-w-0 flex-1 truncate text-slate-600">{r.vehicle} · {r.site_name} · <span className="font-mono text-xs">{r.stall}</span></span>
          <Tag tone={resTone(r.status)}>{r.status}</Tag>
        </li>
      ))}
    </ul>
  );
}

function GateRecent({ rows, go }) {
  if (!rows.length) return <EmptyState icon={ScanLine} testid="fo-cc-gate-empty" title="No gate events yet" body="When your trucks pass a FleetPods gate camera, every entry and exit lands here with a tamper-evident hash." />;
  return (
    <ul className="divide-y divide-slate-100" data-testid="fo-cc-gate">
      {rows.map((g) => (
        <li key={g.id} className="grid grid-cols-[auto_1fr_auto] items-center gap-3 px-5 py-3 text-sm">
          <span className="font-mono text-sm font-semibold tracking-wider text-[#0A234A]">{g.plate}</span>
          <span className="truncate text-xs text-slate-500">{g.booking_ref} · {g.site} · {when(g.created_at)} · <span className="font-mono">{String(g.hash).slice(0, 10)}…</span></span>
          <Tag tone={OUT_TONE[g.outcome] || "mute"}>{String(g.outcome).replace(/_/g, " ")}</Tag>
        </li>
      ))}
      <li className="px-5 py-2.5"><button data-testid="fo-cc-gate-all" onClick={() => go("gate")} className="text-xs font-semibold text-[#FF5722] hover:underline">Open gate proof →</button></li>
    </ul>
  );
}

function Telematics({ t, go }) {
  const [live, setLive] = useState(null);
  useEffect(() => { if (t.connected) { foApi.live().then(setLive).catch(() => setLive({ vehicles: [] })); } }, [t.connected]);
  if (!t.connected) {
    return (
      <Panel title="Telematics" testid="fo-cc-telematics" className="p-0">
        <div className="p-5">
          <EmptyState icon={Radio} testid="fo-cc-telematics-empty" title="Not connected" body="Connect Samsara or Geotab for live positions, hours-of-service and detention detection.">
            <Button variant="navy" testid="fo-cc-telematics-connect" onClick={() => go("connections")}>Connect telematics</Button>
          </EmptyState>
        </div>
      </Panel>
    );
  }
  return (
    <Panel title="Telematics" testid="fo-cc-telematics" className="fo-live-ring" action={<Tag tone="ok">live · {t.provider}</Tag>}>
      <div className="grid grid-cols-2 gap-4 p-5">
        <div><p className="text-xs text-slate-500">Vehicles reporting</p><p className="font-mono text-2xl font-semibold" data-testid="fo-cc-live-count">{live ? live.vehicles.length : "…"}</p></div>
        <div><p className="text-xs text-slate-500">Drivers on HOS</p><p className="font-mono text-2xl font-semibold">{live?.hos?.drivers ?? "…"}</p></div>
      </div>
    </Panel>
  );
}

export default function CommandCenter({ data, go }) {
  const k = data.kpis;
  const onsite = data.reservations.filter((r) => r.status === "onsite");
  const upcoming = data.reservations.filter((r) => ["booked", "verified"].includes(r.status)).slice(0, 6);
  return (
    <>
      <PageHead eyebrow="Command Center" title={data.company} sub="Every truck, driver, stall and gate event in one place. Real records only.">
        <Button variant="primary" testid="fo-cc-book" onClick={() => go("parking")}><SquareParking className="h-4 w-4" />Book parking</Button>
      </PageHead>
      <div className="fo-rise fo-rise-1 mb-6 grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
        <Kpi testid="fo-kpi-vehicles" label="Active vehicles" value={`${k.vehicles_active}/${k.vehicles}`} />
        <Kpi testid="fo-kpi-drivers" label="Drivers" value={k.drivers} hint={k.licences_expired ? `${k.licences_expired} expired licence` : undefined} />
        <Kpi testid="fo-kpi-onsite" label="On site now" value={k.on_site_now} tone="accent" />
        <Kpi testid="fo-kpi-bookings" label="Active bookings" value={k.reservations_active} hint={`${money(k.spend_committed)} committed`} />
        <Kpi testid="fo-kpi-spend" label="Spend this month" value={money(k.spend_month)} />
        <Kpi testid="fo-kpi-alerts" label="Open alerts" value={k.alerts_open} tone={k.alerts_critical ? "bad" : undefined} hint={k.alerts_critical ? `${k.alerts_critical} critical` : undefined} />
      </div>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <Panel className="fo-rise fo-rise-2 xl:col-span-2" title="Needs attention" testid="fo-cc-attention-panel" action={<button data-testid="fo-cc-alerts-all" onClick={() => go("alerts")} className="text-xs font-semibold text-[#FF5722] hover:underline">All alerts</button>}>
          <Attention alerts={data.alerts} go={go} />
        </Panel>
        <div className="fo-rise fo-rise-2"><Telematics t={data.telematics} go={go} /></div>
        <Panel className="fo-rise fo-rise-3" title={`On site now · ${onsite.length}`} testid="fo-cc-onsite-panel"><ResList rows={onsite} testid="fo-cc-onsite" empty="No trucks are parked at a FleetPods site right now." /></Panel>
        <Panel className="fo-rise fo-rise-3" title="Upcoming arrivals" testid="fo-cc-upcoming-panel"><ResList rows={upcoming} testid="fo-cc-upcoming" empty="No upcoming bookings. Book secure parking to see them here." /></Panel>
        <Panel className="fo-rise fo-rise-4" title="Recent gate activity" testid="fo-cc-gate-panel" action={<CircleAlert className="h-4 w-4 text-slate-300" />}><GateRecent rows={data.gate_recent} go={go} /></Panel>
      </div>
    </>
  );
}
